import { Badge, Card, Code, Group, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Fragment, useState } from 'react';
import { api, unwrap } from '../api.ts';
import { changedFields } from '../format.ts';
import { Empty, Failure, Loading, Page } from '../ui.tsx';

const ACTION_COLOR = { create: 'green', update: 'blue', delete: 'red' } as const;

export function AuditPage() {
  const search = useSearch({ from: '/audit' });
  const navigate = useNavigate({ from: '/audit' });
  const setSearch = (next: Partial<typeof search>) =>
    void navigate({ search: (previous) => ({ ...previous, ...next }), replace: true });
  const entries = useQuery({
    queryKey: ['audit'],
    queryFn: () => unwrap(api().GET('/v1/audit-log', { params: { query: { limit: 500 } } })),
  });
  const users = useQuery({ queryKey: ['users'], queryFn: () => unwrap(api().GET('/v1/users')) });
  const [open, setOpen] = useState<string | null>(null);
  const who = (id: string | null) => {
    if (!id) return 'System';
    const user = users.data?.find((candidate) => candidate.id === id);
    return user?.displayName ?? user?.email ?? 'Budovan staff';
  };
  const entities = [...new Set(entries.data?.map((entry) => entry.entity))].toSorted();
  const needle = (search.q ?? '').toLowerCase();
  const shown = (entries.data ?? []).filter(
    (entry) =>
      (!search.entity || entry.entity === search.entity) &&
      (needle === '' ||
        entry.entityId.toLowerCase().includes(needle) ||
        JSON.stringify(entry.after ?? entry.before ?? {})
          .toLowerCase()
          .includes(needle)),
  );
  return (
    <Page
      title="Audit log"
      description="Every change to tenancy data and access, with who made it and what changed."
    >
      <Card withBorder padding="lg">
        <Stack>
          <Group>
            <Select
              placeholder="All entities"
              clearable
              data={entities}
              value={search.entity ?? null}
              onChange={(entity) => setSearch({ entity: entity ?? undefined })}
              w={200}
              aria-label="Entity"
            />
            <TextInput
              leftSection={<IconSearch size={16} />}
              placeholder="Search values"
              value={search.q ?? ''}
              onChange={(event) => setSearch({ q: event.currentTarget.value || undefined })}
              w={260}
            />
          </Group>
          {entries.isPending ? <Loading /> : null}
          <Failure error={entries.error} />
          {entries.data && shown.length === 0 ? <Empty>Nothing matches.</Empty> : null}
          {shown.length > 0 ? (
            <Table.ScrollContainer minWidth={720}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>When</Table.Th>
                    <Table.Th>Who</Table.Th>
                    <Table.Th>Action</Table.Th>
                    <Table.Th>What</Table.Th>
                    <Table.Th>Changed</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {shown.map((entry) => (
                    <Fragment key={entry.id}>
                      <Table.Tr
                        onClick={() => setOpen(open === entry.id ? null : entry.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <Table.Td>{new Date(entry.at).toLocaleString()}</Table.Td>
                        <Table.Td>{who(entry.actorUserId)}</Table.Td>
                        <Table.Td>
                          <Badge
                            variant="light"
                            color={ACTION_COLOR[entry.action as keyof typeof ACTION_COLOR] ?? 'gray'}
                          >
                            {entry.action}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          {entry.entity} <Code>{entry.entityId.slice(0, 8)}</Code>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {changedFields(entry.before, entry.after).join(', ') || '—'}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                      {open === entry.id ? (
                        <Table.Tr>
                          <Table.Td colSpan={5}>
                            <Group align="flex-start" grow>
                              <div>
                                <Text size="xs" c="dimmed" mb={4}>
                                  Before
                                </Text>
                                <Code block>{JSON.stringify(entry.before, null, 2)}</Code>
                              </div>
                              <div>
                                <Text size="xs" c="dimmed" mb={4}>
                                  After
                                </Text>
                                <Code block>{JSON.stringify(entry.after, null, 2)}</Code>
                              </div>
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      ) : null}
                    </Fragment>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          ) : null}
        </Stack>
      </Card>
    </Page>
  );
}
